/**
 * ThreadNode — expandable Thread row component.
 *
 * Props:
 *   item        — raw Airtable Thread record { id, fields }
 *   projectId   — project record ID (for Slack)
 *   projectName — project name string (for Slack panel title)
 *   meetingId   — when set, this thread is being shown as a meeting's minutes line item
 *                 (ThreadCategoryList inside MeetingsSection) — adds a "Remove from meeting"
 *                 menu action that unlinks the thread from THIS meeting only (does not
 *                 delete the thread or touch its other meetings).
 */

import { useState, useEffect, useRef } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { getThreadActivities, deleteThreadCascade, deleteThreadActivity, uploadThreadAttachment, deleteThreadAttachment, threadStatus } from '../services/threads.js'
import { getThreadActionItems, createThreadActionItem, updateThreadActionItem, deleteThreadActionItem, toggleThreadActionItem } from '../services/threadActionItems.js'
import { removeThreadFromMeeting } from '../services/meetings.js'
import { getMemberName, getFirst } from '../outline/utils.js'
import WorkingNotesSection from './WorkingNotesSection.jsx'
import CrewInline from './CrewInline.jsx'
import SlackThreadPanel from './SlackThreadPanel.jsx'
import ThreadCaMount from './ThreadCaMount.jsx'
import { confirmDialog, alertDialog, promptDialog } from './Dialog.jsx'

/* ── Constants ───────────────────────────────────────────────────────────── */

// Status color — match by substring since the status string includes an emoji.
// Three statuses only since 2026-09-10 (threadStatus in services/threads.js): "🟢 Open",
// "⚪ Closed", "🛑 STUCK". Stale and OVERDUE are retired concepts and never reach the screen.
function getStatusColor(status) {
  if (!status) return '#7a9aaa'
  if (status.includes('Open'))    return '#1D9E75'
  if (status.includes('Closed'))  return '#7a9aaa'
  if (status.includes('STUCK'))   return '#dc2626'
  return '#7a9aaa'
}

// Strip leading emoji + space from status for display
// "🟢 Open" → "Open", "⏰ OVERDUE" → "OVERDUE"
function displayStatus(status) {
  if (!status) return ''
  return status.replace(/^\S+\s/, '').trim() || status
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmtDate(d) {
  const dp = (d || '').split('-')
  if (dp.length === 3) return MONTHS[parseInt(dp[1]) - 1] + ' ' + parseInt(dp[2])
  return d || ''
}

/* "Recorded by" / "Created By" link to the MEMBERS table (tblzax2FPEul41fjN), not Contacts.
   getMemberName() checks ctx.membersCache first and falls back to ctx.teamContacts, but falls
   back to the raw record id if neither cache has it — guard that here so an unresolved id never
   renders as "recXXX" text. */
function resolveMemberName(id) {
  if (!id) return ''
  const name = getMemberName(id)
  return (name && name !== id) ? name : ''
}

/* Threads carry no target date of their own (Matt, 2026-09-10 — docs/PASSENGERS.md). A thread
   riding a milestone as a passenger takes that plane's target finish; a thread not yet paired has
   no date, by design. `Milestone` on the thread is the pairing link api/passengers.js writes. */
function ridingFinish(f) {
  const msId = (f['Milestone'] || [])[0]
  if (!msId) return null
  const ms = (ctx.state && ctx.state.milestones || []).find(m => m.id === msId)
  if (!ms) return null
  const mf = ms.fields || {}
  return { date: mf['Latest Finish (LFT)'] || '', name: (mf['Name (from Deliverable)'] || [])[0] || mf['Name'] || 'its milestone' }
}

/* ── Activity Log section ────────────────────────────────────────────────── */

/* Map raw Thread Activity records to row models. threadCount: the legacy
   meeting workflow attaches ONE activity record to every thread touched in a
   meeting — when > 1 the row shows a "shared" marker so a note written about
   one thread isn't mistaken as specific to this one. */
function mapActivityRecords(records) {
  return records.map(r => ({
    id:          r.id,
    type:        r.fields['Activity Type'] || '',
    date:        r.fields['Activity Date'] || '',
    notes:       r.fields['Notes'] || '',
    recordedBy:  (r.fields['Recorded by'] || [])[0] || '',  // lowercase 'b'
    threadCount: (r.fields['Thread'] || []).length,
  }))
}

/* Read-only activity note. Editing happens in a small modal (the row's ⋯ menu → Edit note), not
   inline — the old inline textarea saved on blur, which could hang the app on exit. */
function ActivityNote({ act }) {
  return (
    <div className="wf-node-text"
      style={{ color: act.notes ? 'rgba(7,39,45,0.7)' : '#9bb2b7', fontStyle: act.notes ? 'normal' : 'italic', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', marginTop: 2, lineHeight: 1.45 }}>
      {act.notes || 'No note.'}
    </div>
  )
}

/* One activity row — the canonical ⋯ menu (Edit note) lives in the ell column like every other
   outline node. Edit note opens a small modal (promptDialog) with explicit Save/Cancel. */
function ActivityRow({ act, threadId }) {
  const menu = useActionMenu()
  const recName = resolveMemberName(act.recordedBy)
  function editActivity() {
    window.dispatchEvent(new CustomEvent('open-edit-thread-activity-modal', {
      detail: { activityId: act.id, activityType: act.type, threadId, currentDate: act.date || '', currentNote: act.notes || '' },
    }))
  }
  async function deleteActivity() {
    if (!(await confirmDialog({ title: 'Delete activity?', message: `Delete this "${act.type || 'Activity'}" log entry? This cannot be undone.`, confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteThreadActivity(act.id)
      const arr = ctx.threadActCache && ctx.threadActCache[threadId]
      if (Array.isArray(arr)) { const idx = arr.findIndex(a => a.id === act.id); if (idx !== -1) arr.splice(idx, 1) }
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { await alertDialog({ title: 'Delete failed', message: (e && e.message) || 'Could not delete the activity.' }) }
  }
  return (
    <div className="wf-node wf-node--item" data-key={'thread-act:' + act.id}>
      <div className="wf-row" style={{ alignItems: 'flex-start' }}>
        <span className="wf-triangle-spacer"></span>
        <span className="wf-bullet wf-bullet--item" style={{ marginTop: 6 }}></span>
        <div className="wf-text-wrap" style={{ minWidth: 0, flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 0, width: '100%' }}>
            <span className="wf-node-text" style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <span style={{ fontWeight: 600 }}>{act.type || 'Activity'}</span>
              {act.threadCount > 1 && (
                <span
                  title={'This activity record is linked to ' + act.threadCount + ' threads (legacy meeting workflow) — the note may pertain to another thread.'}
                  style={{ marginLeft: 6, fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.05em', color: '#b8540a', textTransform: 'uppercase' }}
                >shared · {act.threadCount} threads</span>
              )}
            </span>
            {recName && (
              <span style={{ opacity: 0.55, marginLeft: 8, flexShrink: 0, whiteSpace: 'nowrap' }}>{recName}</span>
            )}
            {act.date && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', marginLeft: 8, flexShrink: 0 }}>
                {fmtDate(act.date)}
              </span>
            )}
          </div>
          <ActivityNote act={act} />
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={editActivity}>Edit activity</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={deleteActivity} danger>Delete</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/* Read-only "Thread Opened" row synthesized from the thread Description when the thread has no real
   'Thread Opened' activity (info-only items recorded straight to a closure keep their substance in
   Description). Mirrors the meeting-minutes PDF so the content doesn't disappear in-app. To edit it,
   use Edit thread. */
function SyntheticOpeningRow({ description, date, threadId, createdBy }) {
  const createdByName = resolveMemberName(createdBy)
  return (
    <div className="wf-node wf-node--item" data-key={'thread-opened:' + threadId}>
      <div className="wf-row" style={{ alignItems: 'flex-start' }}>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ marginTop: 6 }}></span>
        <div className="wf-text-wrap" style={{ minWidth: 0, flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', width: '100%' }}>
            <span className="wf-node-text" style={{ flex: 1, minWidth: 0 }}><span style={{ fontWeight: 600 }}>Thread Opened</span></span>
            {createdByName && (
              <span style={{ opacity: 0.55, marginLeft: 8, flexShrink: 0, whiteSpace: 'nowrap' }}>{createdByName}</span>
            )}
            {date && <span className="wf-date-btn" style={{ pointerEvents: 'none', marginLeft: 8, flexShrink: 0 }}>{fmtDate(date)}</span>}
          </div>
          <div className="wf-node-text" style={{ color: 'rgba(7,39,45,0.7)', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', marginTop: 2, lineHeight: 1.45 }}>
            {description}
          </div>
        </div>
      </div>
    </div>
  )
}

// Subtasks are OFF (Matt, 2026-09-09): threads ride milestones as passengers now (docs/PASSENGERS.md),
// and breaking a thread into subtasks gives it more attention than it should get. A flag, not a
// deletion — the section, its rows and the Thread Action Items service stay intact, so turning them
// back on is a one-word change. fastTrackThread still completes any subtasks a thread already has.
const THREAD_SUBTASKS_ENABLED = false

/* ── Subtasks (Round 21) ──────────────────────────────────────────────────
 * "Thread Action Items" — a pre-existing table from the original Softr-era thread page
 * (examples/thread_details.html), reused as-is. No per-subtask assignee: delegation was
 * removed entirely when RASCI was retired (docs/FLOW_RECONCILIATION_PLAN.md decision 6) —
 * the thread's crew owns its subtasks together, and crews are set on the Flight Board only.
 * No drag-and-drop reordering — new items append at the end, matching every other list in
 * the app; Sort Order is still stored so reordering could be added later.
 */

function SubtaskRow({ item, threadId }) {
  const menu = useActionMenu()
  const f = item.fields
  const isDone = !!f['Complete']

  async function toggleComplete(e) {
    e.stopPropagation()
    const next = !isDone
    f['Complete'] = next
    window.dispatchEvent(new Event('outline-render'))
    try {
      await toggleThreadActionItem(item.id, next, wf.currentMemberId)
      f['Completed By'] = (next && wf.currentMemberId) ? [wf.currentMemberId] : []
      window.dispatchEvent(new Event('outline-render'))
    } catch (e2) {
      f['Complete'] = !next
      window.dispatchEvent(new Event('outline-render'))
      alertDialog({ title: 'Save failed', message: e2.message || String(e2) })
    }
  }

  async function editName() {
    const val = await promptDialog({ title: 'Edit subtask', dialogId: 'thread-subtask-name', defaultValue: f['Name'] || '', confirmLabel: 'Save' })
    if (val === null) return
    const next = val.trim()
    if (!next || next === (f['Name'] || '')) return
    try {
      await updateThreadActionItem(item.id, { 'Name': next })
      f['Name'] = next
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Save failed', message: e.message || String(e) }) }
  }

  async function del() {
    if (!(await confirmDialog({ title: 'Delete subtask?', message: `Delete "${f['Name'] || 'this subtask'}"?`, confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteThreadActionItem(item.id)
      const arr = ctx.threadActionItemCache && ctx.threadActionItemCache[threadId]
      if (Array.isArray(arr)) { const idx = arr.findIndex(i => i.id === item.id); if (idx !== -1) arr.splice(idx, 1) }
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Delete failed', message: e.message || String(e) }) }
  }

  return (
    <div className={`wf-node wf-node--item${isDone ? ' wf-node--done' : ''}`} data-key={'subtask:' + item.id}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <button className={`wf-bullet wf-bullet--item${isDone ? ' wf-bullet--done' : ''}`} onClick={toggleComplete} title={isDone ? 'Mark incomplete' : 'Mark complete'}></button>
        <div className="wf-text-wrap">
          <span className={`wf-node-text${isDone ? ' wf-node-text--done' : ''}`} onClick={editName} style={{ cursor: 'pointer' }}>{f['Name'] || 'Subtask'}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={editName}>Edit name</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={del} danger>Delete</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

function ThreadSubtasksSection({ threadId }) {
  const key = 'thread-subtasks:' + threadId
  const isCollapsed = wf.collapsed[key] !== false
  const items = ctx.threadActionItemCache?.[threadId]  // undefined=not started, null=in-flight, []=loaded
  const [newName, setNewName] = useState('')

  function fetchItems() {
    if (!ctx.threadActionItemCache) ctx.threadActionItemCache = {}
    ctx.threadActionItemCache[threadId] = null
    getThreadActionItems(threadId).then(records => {
      if (!ctx.threadActionItemCache) ctx.threadActionItemCache = {}
      ctx.threadActionItemCache[threadId] = records
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.threadActionItemCache) ctx.threadActionItemCache = {}
      ctx.threadActionItemCache[threadId] = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  useEffect(() => {
    if (isCollapsed) return
    if (items !== undefined && items !== null) return
    fetchItems()
  }, [isCollapsed, threadId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-trigger load after cache bust (mirrors ThreadActivitySection's own pattern)
  useEffect(() => {
    function onRender() {
      if (wf.collapsed[key] !== false) return
      if (!ctx.threadActionItemCache) return
      if (ctx.threadActionItemCache[threadId] === undefined) fetchItems()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [threadId, key]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !isCollapsed; window.dispatchEvent(new Event('outline-render')) }

  async function addItem() {
    const name = newName.trim()
    if (!name) return
    setNewName('')
    const arr = Array.isArray(items) ? items : []
    const maxSort = arr.reduce((m, i) => Math.max(m, i.fields['Sort Order'] || 0), 0)
    const newSort = maxSort > 0 ? maxSort + 1000 : 1000
    try {
      const rec = await createThreadActionItem(threadId, name, newSort)
      if (ctx.threadActionItemCache) {
        const list = ctx.threadActionItemCache[threadId]
        if (Array.isArray(list)) list.push(rec)
      }
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Add failed', message: e.message || String(e) }) }
  }

  const done = Array.isArray(items) ? items.filter(i => i.fields['Complete']).length : 0
  const total = Array.isArray(items) ? items.length : null

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Subtasks"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Subtasks</span>
          {total != null && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{done}/{total}</span>}
        </div>
      </div>

      {!isCollapsed && (
        <div className="wf-children">
          {(items === null || items === undefined) && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                <div className="wf-text-wrap">
                  <span className="wf-node-text" style={{ color: '#7a9aaa' }}>Loading…</span>
                </div>
              </div>
            </div>
          )}
          {Array.isArray(items) && items.map(item => (
            <SubtaskRow key={item.id} item={item} threadId={threadId} />
          ))}
          {Array.isArray(items) && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" style={{ opacity: 0.3 }}></span>
                <div className="wf-text-wrap">
                  <input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addItem(); if (e.key === 'Escape') setNewName('') }}
                    placeholder="+ Add subtask…"
                    style={{ border: 'none', outline: 'none', background: 'none', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: '#07272D', width: '100%', padding: 0 }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ThreadActivitySection({ threadId, threadTitle, description, initiated, createdBy }) {
  const key = 'thread-acts:' + threadId
  const isCollapsed = wf.collapsed[key] !== false
  const acts = ctx.threadActCache?.[threadId]  // undefined=not started, null=in-flight, []=loaded
  const menu = useActionMenu()
  const desc = (description || '').trim()
  const showSynthetic = Array.isArray(acts) && !!desc && !acts.some(a => a.type === 'Thread Opened')

  // Load activities when expanded
  useEffect(() => {
    if (isCollapsed) return
    // "Recorded by" / "Created By" link to Members (ctx.membersCache), not Contacts — loaded
    // once at app startup, but guard here too in case that hasn't happened yet.
    if (!ctx.membersCache && ctx.loadMembers) {
      ctx.loadMembers(() => window.dispatchEvent(new Event('outline-render')))
    }
    if (acts !== undefined && acts !== null) return  // already loaded or loading
    if (!ctx.threadActCache) ctx.threadActCache = {}
    ctx.threadActCache[threadId] = null
    getThreadActivities(threadId).then(records => {
      if (!ctx.threadActCache) ctx.threadActCache = {}
      ctx.threadActCache[threadId] = mapActivityRecords(records)
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.threadActCache) ctx.threadActCache = {}
      ctx.threadActCache[threadId] = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }, [isCollapsed, threadId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-trigger load after cache bust (e.g. after logging a new activity)
  useEffect(() => {
    function onRender() {
      if (wf.collapsed[key] !== false) return
      if (!ctx.threadActCache) return
      if (ctx.threadActCache[threadId] === undefined) {
        ctx.threadActCache[threadId] = null
        getThreadActivities(threadId).then(records => {
          if (!ctx.threadActCache) ctx.threadActCache = {}
          ctx.threadActCache[threadId] = mapActivityRecords(records)
          window.dispatchEvent(new Event('outline-render'))
        }).catch(() => {
          if (!ctx.threadActCache) ctx.threadActCache = {}
          ctx.threadActCache[threadId] = []
          window.dispatchEvent(new Event('outline-render'))
        })
      }
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [threadId, key])

  function handleAddActivity() {
    window.dispatchEvent(new CustomEvent('open-thread-activity-modal', {
      detail: { threadId, threadTitle, threadStatus: status }
    }))
  }

  function toggleCollapse() {
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={e => { e.stopPropagation(); toggleCollapse() }}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Activity Log"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Activity Log</span>
          {Array.isArray(acts) && (
            <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{acts.length}</span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleAddActivity}>Log Activity</ActionMenuItem>
      </ActionMenu>

      {!isCollapsed && (
        <div className="wf-children">
          {(acts === null || acts === undefined) && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                <div className="wf-text-wrap">
                  <span className="wf-node-text" style={{ color: '#7a9aaa' }}>Loading…</span>
                </div>
              </div>
            </div>
          )}
          {Array.isArray(acts) && acts.length === 0 && !showSynthetic && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                <div className="wf-text-wrap">
                  <span className="wf-node-text" style={{ color: '#7a9aaa', fontStyle: 'italic' }}>No activity logged yet.</span>
                </div>
              </div>
            </div>
          )}
          {Array.isArray(acts) && acts.map(act => (
            <ActivityRow key={act.id} act={act} threadId={threadId} />
          ))}
          {showSynthetic && <SyntheticOpeningRow description={desc} date={initiated} threadId={threadId} createdBy={createdBy} />}
        </div>
      )}
    </div>
  )
}

/* ── Attachments (Round 21) ───────────────────────────────────────────────
 * Reads/writes the Thread's own native Airtable Attachments field directly (no separate
 * cache/table) — sketches and other files for whoever picks up the thread's changes.
 */

function AttachmentRow({ att, onRemove }) {
  const menu = useActionMenu()
  return (
    <div className="wf-node wf-node--item" data-key={'thread-att:' + att.id}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <a href={att.url} target="_blank" rel="noopener noreferrer" className="wf-node-text" style={{ color: '#2563eb', textDecoration: 'none' }}>
            📎 {att.filename || 'Attachment'}
          </a>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={onRemove} danger>Remove</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

function ThreadAttachmentsSection({ threadId, attachments, onChange }) {
  const key = 'thread-atts:' + threadId
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  function toggleCollapse(e) { e.stopPropagation(); wf.collapsed[key] = !isCollapsed; window.dispatchEvent(new Event('outline-render')) }
  function pickFile() { fileInputRef.current && fileInputRef.current.click() }

  async function handleFile(e) {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    try {
      const updated = await uploadThreadAttachment(threadId, file, attachments)
      onChange(updated.fields['Attachments'] || [])
    } catch (err) { alertDialog({ title: 'Upload failed', message: err.message || String(err) }) }
    finally { setUploading(false) }
  }

  async function removeAttachment(att) {
    if (!(await confirmDialog({ title: 'Remove attachment?', message: `Remove "${att.filename || 'this file'}"?`, confirmLabel: 'Remove', danger: true }))) return
    try {
      const updated = await deleteThreadAttachment(threadId, att.id, attachments)
      onChange(updated.fields['Attachments'] || [])
    } catch (err) { alertDialog({ title: 'Remove failed', message: err.message || String(err) }) }
  }

  const list = Array.isArray(attachments) ? attachments : []

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Attachments"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Attachments</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{uploading ? 'Uploading…' : list.length}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); pickFile() }}>Add attachment</ActionMenuItem>
      </ActionMenu>
      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFile} />
      {!isCollapsed && (
        <div className="wf-children">
          {list.length === 0 && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                <div className="wf-text-wrap">
                  <span className="wf-node-text" style={{ color: '#7a9aaa', fontStyle: 'italic' }}>No attachments yet.</span>
                </div>
              </div>
            </div>
          )}
          {list.map(att => <AttachmentRow key={att.id} att={att} onRemove={() => removeAttachment(att)} />)}
        </div>
      )}
    </div>
  )
}

/* ── Main ThreadNode ─────────────────────────────────────────────────────── */

/* onFocus: replaces the bullet's in-place zoom (EntityPeek, which may show another project's thread).
   hideCaMount: set by CaThreadMount (the CA-item → thread direction) so the
   thread doesn't mount the CA item right back — breaks the render recursion. */
export default function ThreadNode({ item, projectId, projectName, hideCaMount, detailZoom, labelPrefix = '', meetingId, onFocus }) {
  // Defensive: never crash the page render on a malformed/missing thread record.
  if (!item || !item.fields || !item.id) return null
  const f        = item.fields
  const threadId = item.id
  const title    = f['Title'] || 'Thread'
  const status   = threadStatus(f)
  const ride     = ridingFinish(f)
  const key      = 'thread:' + threadId
  const isCollapsed = wf.collapsed[key] !== false
  const isClosed    = status.includes('Closed')
  const menu = useActionMenu()

  // Slack Thread Time Stamp — note Airtable field uses two words "Time Stamp"
  const [showPanel,     setShowPanel]    = useState(false)
  const [localThreadTs, setLocalThreadTs]= useState(f['Slack Thread Time Stamp'] || '')

  const threadTs   = localThreadTs || f['Slack Thread Time Stamp'] || ''
  const storedTs   = typeof window !== 'undefined' ? localStorage.getItem('slack:read:' + threadId) : null
  const hasUnread  = !!(threadTs && storedTs !== threadTs)

  // In detail zoom the ⋯ menu lives on the ZoomTitle; its Delete dispatches 'thread-delete'. Only the
  // zoomed node listens, so the cascade-delete + cache busting stays here in the canonical component.
  useEffect(() => {
    if (!detailZoom) return
    function onDelete(e) { if (e.detail && e.detail.threadId === threadId) handleDelete() }
    window.addEventListener('thread-delete', onDelete)
    return () => window.removeEventListener('thread-delete', onDelete)
  }, [detailZoom, threadId]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleAddActivity() {
    window.dispatchEvent(new CustomEvent('open-thread-activity-modal', {
      detail: { threadId, threadTitle: title, threadStatus: status }
    }))
  }

  // Unlink the thread from THIS meeting only (e.g. it wasn't discussed) — the thread stays open
  // on the project and on any other meetings; nothing about the thread itself is touched.
  async function handleRemoveFromMeeting() {
    if (!meetingId) return
    if (!(await confirmDialog({
      title: 'Remove from this meeting?',
      message: 'This removes the thread from this meeting’s minutes (e.g. it wasn’t discussed) — it stays open on the project and can be added back later. The thread itself is not deleted.',
      confirmLabel: 'Remove from meeting',
    }))) return
    try {
      const meeting = (ctx.meetingCache || []).find(m => m.id === meetingId)
      const currentIds = meeting ? (meeting.fields['Threads'] || []) : []
      await removeThreadFromMeeting(meetingId, threadId, currentIds)
      if (meeting) meeting.fields['Threads'] = currentIds.filter(id => id !== threadId)
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) {
      alertDialog({ title: 'Remove failed', message: e.message || String(e) })
    }
  }

  async function handleDelete() {
    if (!(await confirmDialog({
      title: 'Delete this thread permanently?',
      message: 'This also removes its activity log. ' +
        'If this thread tracks a submittal or RFI, that record and its full review history and files are deleted too.\n\n' +
        'This cannot be undone.',
      confirmLabel: 'Delete thread', danger: true,
    }))) return
    try { await deleteThreadCascade(threadId) }
    catch (e) { alertDialog({ title: 'Delete failed', message: e.message || String(e) }); return }
    // Drop it from the live caches so it disappears immediately; clear CA + submittal/RFI caches
    // since a linked tracking record may have been removed too.
    if (Array.isArray(ctx.threadCache)) ctx.threadCache = ctx.threadCache.filter(t => t.id !== threadId)
    if (ctx.caThreadCache) delete ctx.caThreadCache[threadId]
    ctx.submittalCache = {}
    ctx.rfiCache = {}
    window.dispatchEvent(new Event('outline-render'))
  }

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  const statusColor = getStatusColor(status)

  // Shared elements reused by the normal row and the detail-zoom view.
  const slackBtnEl = projectId ? (
    <button
      className={`wf-slack-btn${threadTs ? ' wf-slack-btn--active' : ''}`}
      title={threadTs ? 'View discussion' : 'Start a discussion in Slack'}
      onClick={e => { e.stopPropagation(); setShowPanel(true) }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
      {hasUnread && <span className="wf-slack-unread-dot" />}
    </button>
  ) : null

  const slackPanelEl = (showPanel && projectId) ? (
    <SlackThreadPanel
      entityId={threadId}
      entityType="thread"
      entityName={title}
      projectId={projectId}
      projectName={projectName || ''}
      threadTs={threadTs}
      onClose={() => setShowPanel(false)}
      onThreadStarted={ts => {
        setLocalThreadTs(ts)
        item.fields['Slack Thread Time Stamp'] = ts
      }}
    />
  ) : null

  const menuEl = (
    <ActionMenu menu={menu}>
      <ActionMenuItem onClick={handleAddActivity}>Log Activity</ActionMenuItem>
      <ActionMenuDivider />
      <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-create-thread-modal', { detail: { threadId, projectId } }))}>Edit thread</ActionMenuItem>
      {!isClosed && <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-move-threads-modal', { detail: { threadId, projectId: projectId || getFirst(f['Project']) } }))}>Move to a milestone…</ActionMenuItem>}
      {meetingId && <ActionMenuDivider />}
      {meetingId && <ActionMenuItem onClick={handleRemoveFromMeeting}>Remove from meeting</ActionMenuItem>}
      <ActionMenuDivider />
      <ActionMenuItem onClick={handleDelete} danger>Delete thread</ActionMenuItem>
    </ActionMenu>
  )

  // Detail zoom: children-only. The status / team / Slack meta and the ⋯ menu now render inline on
  // the ZoomTitle (see ZoomTitle's threadZoom branch), so they sit on the title line instead of a
  // separate row below it. Delete is triggered from that title menu via the 'thread-delete' event.
  if (detailZoom) {
    return (
      <div className="wf-list">
        {!hideCaMount && <ThreadCaMount item={item} projectId={projectId} />}
        {THREAD_SUBTASKS_ENABLED && <ThreadSubtasksSection threadId={threadId} />}
        <ThreadActivitySection threadId={threadId} threadTitle={title} description={f['Description']} initiated={f['Initiated']} createdBy={(f['Created By'] || [])[0] || ''} />
        <WorkingNotesSection entityType="thread" entityId={threadId} />
        <ThreadAttachmentsSection threadId={threadId} attachments={f['Attachments'] || []} onChange={next => { f['Attachments'] = next; window.dispatchEvent(new Event('outline-render')) }} />
      </div>
    )
  }

  return (
    <div
      className={`wf-node wf-node--thread${isCollapsed ? ' wf-node--collapsed' : ''}${isClosed ? ' wf-node--thread-closed' : ''}`}
      data-key={key}
    >
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--ms"
          title="Focus on this thread"
          data-kbzoom="1"
          onClick={e => { e.stopPropagation(); if (onFocus) onFocus(); else ctx.zoomToSection('thread-detail:' + threadId, { root: 'threads' }) }}
        ></button>
        <div className="wf-text-wrap">
          {/* Title — flex: 1 pushes everything after it to the right.
              (No Type badge: the category branch node above the row already
              names the type — Round 7 feedback.) */}
          <span className="wf-node-text wf-node-text--thread">{labelPrefix}{title}</span>
          {/* Status + the ridden plane's target finish — grouped right, same as wf-meta pattern */}
          <span className="wf-meta">
            {status && (
              <span className="wf-status-pill" style={{ color: statusColor, borderColor: statusColor + '33' }}>
                {displayStatus(status)}
              </span>
            )}
            {ride && ride.date && !isClosed && (
              <span className="wf-date-btn" title={'Rides ' + ride.name} style={{ color: '#7a9aaa', pointerEvents: 'none' }}>
                {fmtDate(ride.date)}
              </span>
            )}
          </span>
          {/* Crew chip — shows the flight/squadron when this thread is in the air, else nothing */}
          <CrewInline kind="thread" id={threadId} />
          {/* Slack button — always last in wf-text-wrap */}
          {slackBtnEl}
        </div>
        <ActionMenuButton menu={menu} />
      </div>

      {/* SlackThreadPanel — outside wf-row per placement rule */}
      {slackPanelEl}

      {menuEl}

      {/* Expanded children */}
      {!isCollapsed && (
        <div className="wf-children">
          {/* RFI/Submittal threads mount their CA item — the same canonical
              node as in Reference > Construction (renders null otherwise) */}
          {!hideCaMount && <ThreadCaMount item={item} projectId={projectId} />}
          {THREAD_SUBTASKS_ENABLED && <ThreadSubtasksSection threadId={threadId} />}
          <ThreadActivitySection threadId={threadId} threadTitle={title} description={f['Description']} initiated={f['Initiated']} createdBy={(f['Created By'] || [])[0] || ''} />
          <WorkingNotesSection entityType="thread" entityId={threadId} />
          <ThreadAttachmentsSection threadId={threadId} attachments={f['Attachments'] || []} onChange={next => { f['Attachments'] = next; window.dispatchEvent(new Event('outline-render')) }} />
        </div>
      )}
    </div>
  )
}
